import {
  tag_colorTagSevereText,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_md,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

import { flyoutSurface, paddingHorizontal } from '../billPayFlyoutChrome.style'

/* Chrome shared with PayBillFlyout — see ../billPayFlyoutChrome.style. */
export {
  flyoutTitle,
  footer,
  footerButtons,
  header,
} from '../billPayFlyoutChrome.style'

export default flyoutSurface('VendorFlyout')

export const description = css`
  margin: 0;
  ${font_md}
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextPrimary};
`

/* The form fills the remaining flyout height: scrollable field list + a pinned
   footer that stays put while the fields scroll. */
export const form = css`
  display: flex;
  flex-direction: column;
  flex: auto;
  min-height: 0;
`

export const confirmationSurface = css`
  display: flex;
  flex-direction: column;
  flex: auto;
  min-height: 0;
`

export const fields = css`
  flex: auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing(4)};
  padding: ${spacing(6)} ${paddingHorizontal};
`

export const phoneField = css`
  /*
   * Put the phone field on the same label rhythm as its TextInput neighbours.
   * Every TextInput in this form leaves 8px between its label and its control;
   * this one is hand-assembled from FieldLabel + a row, and measured only 2px,
   * so "Phone Number" sat noticeably tighter to its inputs than any other
   * label. FieldLabel's own fixed margin-bottom is neutralised in favour of an
   * explicit column gap, so the spacing is stated here rather than inherited
   * from a shared component that knows nothing about this layout.
   */
  display: flex;
  flex-direction: column;
  gap: ${spacing(2)};

  > label {
    margin-bottom: 0;
  }

  [role='alert'] span {
    font-family: ${font_family_weight_regular} !important;
  }

  input::placeholder {
    opacity: 1 !important;
    color: ${text_colorTextSecondary} !important;
  }
`

export const phoneRow = css`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`

export const phoneInput = css`
  flex-grow: 2;
`

/* No dedicated `text_colorTextError` token exists in the shipping DS (only
   input_borderError / tag severe / warning families) — using the severe tag
   text token; DS gap flagged owner-tagged in the PR. Error is never conveyed
   by color alone: it is a role="alert" text message. */
export const footerError = css`
  margin: 0;
  ${font_md}
  color: ${tag_colorTagSevereText};
`

/* Edit-mode tab bar (EX-40771). Pads the ext-ui Subnav to the header's own
   horizontal inset so the tab underline lines up with the title. */
export const tabBarWrap = css`
  padding: 0 ${paddingHorizontal};
`

/* Payment Information tab body — a placeholder in this ticket; EX-40772 fills
   it. Same scroll/pad rhythm as the fields column so switching tabs doesn't
   shift the layout. */
export const paymentPlaceholder = css`
  flex: auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
  padding: ${spacing(6)} ${paddingHorizontal};
  color: ${text_colorTextSecondary};
`

export const pendingNote = css`
  margin: ${spacing(3)} 0 0;
  ${font_md}
  color: ${text_colorTextSecondary};
`

/* Acknowledgement footer. Prototype declared values: outer gap-[20px], row
   gap-[12px], text-[14px] leading-[20px], label font-medium on primary text,
   description on secondary text. */
export const ackBlock = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(5)};
`

export const ackRow = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacing(3)};
`

export const ackText = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};
  font-size: 14px;
  line-height: 20px;
`

export const ackPrimary = css`
  font-family: ${font_family_weight_medium};
  color: ${text_colorTextPrimary};
`

export const ackDescription = css`
  color: ${text_colorTextSecondary};
`

export const footerActionsEnd = css`
  display: flex;
  justify-content: flex-end;
`

/**
 * Screen-reader-only. Used for the confirmation live region, which must
 * announce without rendering — design deliberately dropped the visible
 * "confirming" note (see AddVendorFlyout).
 */
export const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`
